import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MatrixCanvas } from '@/components/MatrixCanvas';
import {
  HERO_REVIEW_FAMILIES,
  HERO_REVIEW_PRESENTATIONS,
  RESTAURANT_HERO_STOCK,
  buildHeroReviewBlock,
  suggestedPresentationForTheme,
  suggestedThemeForHero,
  type HeroReviewFamily,
  type HeroReviewPresentation,
  type HeroStockEntry,
} from '@/lib/restaurantHeroStock';
import { THEME_ID } from '@web-studio/shared-schemas';

const THEME_OPTIONS = [
  { id: THEME_ID.michelinDark, label: 'michelin-dark' },
  { id: THEME_ID.limestoneOrangery, label: 'limestone-orangery' },
  { id: THEME_ID.moorlandHearth, label: 'moorland-hearth' },
  { id: THEME_ID.bistroBrasserie, label: 'bistro-brasserie' },
  { id: THEME_ID.hinokiCounter, label: 'hinoki-counter' },
  { id: THEME_ID.lotusSteam, label: 'lotus-steam' },
  { id: THEME_ID.tileAwning, label: 'tile-awning' },
  { id: THEME_ID.lanternCourt, label: 'lantern-court' },
] as const;

type Viewport = 'desktop' | 'tablet' | 'mobile';

function findEntry(id: string | null): HeroStockEntry {
  return RESTAURANT_HERO_STOCK.find((e) => e.id === id) ?? RESTAURANT_HERO_STOCK[0]!;
}

export function HeroStockReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = findEntry(searchParams.get('hero'));
  const [familyFilter, setFamilyFilter] = useState<HeroReviewFamily | 'all'>('all');
  const [surfaceFilter, setSurfaceFilter] = useState<'all' | 'on-light' | 'on-dark'>('all');
  const [presentation, setPresentation] = useState<HeroReviewPresentation>(() =>
    suggestedPresentationForTheme(suggestedThemeForHero(selected)),
  );
  const [themeId, setThemeId] = useState(() => suggestedThemeForHero(selected));
  const [followSuggestedTheme, setFollowSuggestedTheme] = useState(true);
  const [followThemeArrangement, setFollowThemeArrangement] = useState(true);
  const [viewport, setViewport] = useState<Viewport>('desktop');

  const filtered = useMemo(() => {
    return RESTAURANT_HERO_STOCK.filter((e) => {
      if (familyFilter !== 'all' && e.family !== familyFilter) return false;
      if (surfaceFilter !== 'all' && e.surface !== surfaceFilter) return false;
      return true;
    });
  }, [familyFilter, surfaceFilter]);

  const block = useMemo(
    () => buildHeroReviewBlock(selected, presentation),
    [selected, presentation],
  );

  const selectHero = (entry: HeroStockEntry) => {
    const next = new URLSearchParams(searchParams);
    next.set('hero', entry.id);
    setSearchParams(next, { replace: true });
    if (followSuggestedTheme) {
      const nextTheme = suggestedThemeForHero(entry);
      setThemeId(nextTheme);
      if (followThemeArrangement) {
        setPresentation(suggestedPresentationForTheme(nextTheme));
      }
    }
  };

  const applyTheme = (nextTheme: string) => {
    setFollowSuggestedTheme(false);
    setThemeId(nextTheme);
    if (followThemeArrangement) {
      setPresentation(suggestedPresentationForTheme(nextTheme));
    }
  };

  const fileName = selected.path.split('/').pop() ?? selected.path;

  return (
    <div className="lab-page lab-fit-viewport lab-hero-review">
      <header className="lab-fit-viewport__header">
        <h1 className="lab-fit-viewport__title">Restaurant hero stock</h1>
        <p className="lab-fit-viewport__hint">
          Full Bleed trước để đọc crop/tone · rồi thử arrangement của theme
        </p>
      </header>

      <div className="lab-hero-review__layout">
        <aside className="lab-hero-review__rail" aria-label="Hero catalog">
          <div className="lab-hero-review__filters">
            <label className="lab-hero-review__field">
              <span>Family</span>
              <select
                value={familyFilter}
                onChange={(e) =>
                  setFamilyFilter(e.target.value as HeroReviewFamily | 'all')
                }
              >
                <option value="all">All</option>
                {HERO_REVIEW_FAMILIES.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
            <label className="lab-hero-review__field">
              <span>Surface</span>
              <select
                value={surfaceFilter}
                onChange={(e) =>
                  setSurfaceFilter(e.target.value as 'all' | 'on-light' | 'on-dark')
                }
              >
                <option value="all">All</option>
                <option value="on-dark">on-dark</option>
                <option value="on-light">on-light</option>
              </select>
            </label>
          </div>

          <p className="lab-hero-review__count">
            {filtered.length} / {RESTAURANT_HERO_STOCK.length}
          </p>

          <ul className="lab-hero-review__list">
            {filtered.map((entry) => {
              const active = entry.id === selected.id;
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    className={
                      active
                        ? 'lab-hero-review__item lab-hero-review__item--active'
                        : 'lab-hero-review__item'
                    }
                    onClick={() => selectHero(entry)}
                  >
                    <img src={entry.path} alt="" width={96} height={54} loading="lazy" />
                    <span className="lab-hero-review__item-copy">
                      <span className="lab-hero-review__item-title">{entry.label}</span>
                      <span className="lab-hero-review__item-meta">{entry.surface}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="lab-hero-review__stage">
          <div className="lab-hero-review__toolbar">
            <div className="lab-hero-review__controls">
            <label className="lab-hero-review__field">
              <span>Arrangement</span>
              <select
                value={presentation}
                onChange={(e) => {
                  setFollowThemeArrangement(false);
                  setPresentation(e.target.value as HeroReviewPresentation);
                }}
              >
                {HERO_REVIEW_PRESENTATIONS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="lab-hero-review__field">
              <span>Theme</span>
              <select
                value={themeId}
                onChange={(e) => applyTheme(e.target.value)}
              >
                {THEME_OPTIONS.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="lab-hero-review__field">
              <span>Viewport</span>
              <select
                value={viewport}
                onChange={(e) => setViewport(e.target.value as Viewport)}
              >
                <option value="desktop">Desktop</option>
                <option value="tablet">Tablet</option>
                <option value="mobile">Mobile</option>
              </select>
            </label>
            <label className="lab-hero-review__check">
              <input
                type="checkbox"
                checked={followSuggestedTheme}
                onChange={(e) => {
                  setFollowSuggestedTheme(e.target.checked);
                  if (e.target.checked) {
                    const nextTheme = suggestedThemeForHero(selected);
                    setThemeId(nextTheme);
                    if (followThemeArrangement) {
                      setPresentation(suggestedPresentationForTheme(nextTheme));
                    }
                  }
                }}
              />
              Auto theme
            </label>
            <label className="lab-hero-review__check">
              <input
                type="checkbox"
                checked={followThemeArrangement}
                onChange={(e) => {
                  setFollowThemeArrangement(e.target.checked);
                  if (e.target.checked) {
                    setPresentation(suggestedPresentationForTheme(themeId));
                  }
                }}
              />
              Theme layout
            </label>
            </div>
            <p className="lab-hero-review__path" title={selected.path}>
              <code>{fileName}</code>
            </p>
          </div>

          <div className="lab-hero-review__canvas-wrap">
            <MatrixCanvas
              blocks={[block]}
              locale="de"
              themeId={themeId}
              colorsetId=""
              spacingEnum="balanced"
              viewport={viewport}
              compact
            />
          </div>
        </section>
      </div>
    </div>
  );
}
